import React, { useState, useEffect } from 'react';
import Layout from '../elements/Layout';
import moment from 'moment';
import { fetchOrders } from '../utils/api/api';
import Loading from '../elements/Loading';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const getOrders = async () => {
      setLoading(true);
      try {
        const data = await fetchOrders({ page, pageSize });
        setOrders(data.orders);
        setTotalPages(data.meta.totalPages);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    getOrders();
  }, [page, pageSize]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handlePageSizeChange = (e) => {
    setPageSize(e.target.value);
    setPage(1);
  };

  const renderPageNumbers = () => {
    let pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`p-2 mx-1 rounded ${page === i ? 'bg-secondary text-white' : 'bg-gray-200 text-black'}`}
          >
            {i}
          </button>
        );
      }
    } else {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className={`p-2 mx-1 rounded ${page === 1 ? 'bg-secondary text-white' : 'bg-gray-200 text-black'}`}
        >
          1
        </button>
      );

      if (page > 3) {
        pages.push(<span key="left-ellipsis" className="px-2">...</span>);
      }

      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`p-2 mx-1 rounded ${page === i ? 'bg-secondary text-white' : 'bg-gray-200 text-black'}`}
          >
            {i}
          </button>
        );
      }

      if (page < totalPages - 2) {
        pages.push(<span key="right-ellipsis" className="px-2">...</span>);
      }

      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={`p-2 mx-1 rounded ${page === totalPages ? 'bg-secondary text-white' : 'bg-gray-200 text-black'}`}
        >
          {totalPages}
        </button>
      );
    }
    return pages;
  };

  return (
    <Layout text="Orders">
      <div className="overflow-x-auto">
        <div className='mb-2'>
          <label htmlFor="pageSize" className="mr-2">Rows per page:</label>
          <select id="pageSize" value={pageSize} onChange={handlePageSizeChange} className="p-1 border rounded">
            <option value={5}>10</option>
            <option value={10}>40</option>
            <option value={20}>100</option>
          </select>
        </div>
        {loading ? (
          <Loading />
        ) : (
          <table className="min-w-full bg-white border">
            <thead>
              <tr className="text-primary border-b pb-4 text-[12px] py-3 bg-[#F7F7F7] font-medium leading-normal">
                <th className="w-1/5 px-6 py-4 text-left whitespace-nowrap">#</th>
                <th className="w-1/5 px-6 py-4 text-left whitespace-nowrap">Customer Name</th>
                <th className="w-1/5 px-6 py-4 text-left whitespace-nowrap">Property</th>
                <th className="w-1/5 px-6 py-4 text-left whitespace-nowrap">Amount</th>
                <th className="w-1/5 px-6 py-4 text-left whitespace-nowrap">Payment Type</th>
                <th className="w-1/5 px-6 py-4 text-left whitespace-nowrap">Status</th>
                <th className="w-1/5 px-6 py-4 text-left whitespace-nowrap">Created At</th>
              </tr>
            </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map((order, index) => (
                    <React.Fragment key={order.id}>
                      <tr className="bg-gray-100 border-t">
                        <td className="py-4 px-6 text-left whitespace-nowrap text-[12px]">
                          {index + 1 + (page - 1) * pageSize}
                        </td>
                        <td className="py-4 px-6 text-left whitespace-nowrap text-[12px]">
                          {order?.customer?.first_name || ''} {order?.customer?.last_name || ''}
                        </td>
                        <td className="py-4 px-6 text-left whitespace-nowrap text-[12px]">
                          {order?.project?.title || 'N/A'}
                        </td>
                        <td className="py-4 px-6 text-left whitespace-nowrap text-[12px]">
                          KES {Number(order.amount).toLocaleString()}
                        </td>
                        <td className="py-4 px-6 text-left whitespace-nowrap text-[12px]">
                          {order?.payment_type}
                        </td>
                        <td className={`py-4 px-6 text-left whitespace-nowrap ${order?.payment_status === 'pending' ? 'text-yellow-500' : 'text-green-500'}`}>
                          {order?.payment_status}
                        </td>
                        <td className="px-6 py-4 text-left whitespace-nowrap">
                          {moment(order?.createdAt).format('MMM Do YYYY')}
                        </td>
                      </tr>
                      {order.order_items.map((item) => (
                        <tr key={item.id} className="border-t hover:bg-gray-50">
                          <td className="py-2 px-6 text-left whitespace-nowrap text-[12px]"></td>
                          <td className="py-2 px-6 text-left whitespace-nowrap text-[12px]"></td>
                          <td className="py-2 px-6 text-left whitespace-nowrap text-[12px]">
                            {item?.property?.title_number || 'N/A'} ({item?.property?.property_number})
                          </td>
                          <td className="py-2 px-6 text-left whitespace-nowrap text-[12px]">
                            KES {Number(item.amount).toLocaleString()}
                          </td>
                          <td className="py-2 px-6 text-left whitespace-nowrap text-[12px]"></td>
                          <td className="py-2 px-6 text-left whitespace-nowrap text-[12px]"></td>
                          <td className="px-6 py-2 text-left whitespace-nowrap"></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="p-3 text-center text-gray-500">
                      No orders found
                    </td>
                  </tr>
                )}
              </tbody>
          </table>
        )}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
              className="text-primary text-[12px] font-medium"
            >
              Previous
            </button>
            {renderPageNumbers()}
            <button
              disabled={page === totalPages}
              onClick={() => handlePageChange(page + 1)}
              className="text-primary text-[12px] font-medium"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Orders;
